import { computed } from 'vue';
import { useHttpRequest } from '~/composables/useHttpRequest';
import { endpoints } from '~/network/endpoints.js';

// On-the-fly timeline feed (replaces the IndexedDB-synced useTimeline for new
// clients). Cursor-paged; items can shift while scrolling so pages are deduped
// by item id. State is shared so the SignalR handler in AppLayout can flag
// pending updates for the timeline component.
export function useTimelineFeed() {
    const items = useState('timelineFeedItems', () => []);
    const pinned = useState('timelineFeedPinned', () => []);
    const nextCursor = useState('timelineFeedCursor', () => null);
    const hasMore = useState('timelineFeedHasMore', () => false);
    const feedLoading = useState('timelineFeedLoading', () => false);
    // True once the first feed fetch has settled (success or error). Gates the
    // empty/get-started states so they never flash while the request is still
    // in flight — distinct from feedLoading, which toggles on every refresh.
    const feedLoaded = useState('timelineFeedLoaded', () => false);
    const loadingMore = useState('timelineFeedLoadingMore', () => false);
    const feedErrors = useState('timelineFeedErrors', () => null);
    const dismissingLoading = useState('timelineFeedDismissing', () => ({}));

    // Home sections that ride on the feed's FIRST page (empty/zero on later
    // pages). Mirrors TimelineFeedResponse in the Flutter app. Each Home section
    // renders only when its slice has content.
    const managedClubs = useState('timelineFeedManagedClubs', () => []);
    const leagues = useState('timelineFeedLeagues', () => []);
    const ladders = useState('timelineFeedLadders', () => []);
    const tournaments = useState('timelineFeedTournaments', () => []);
    const americanos = useState('timelineFeedAmericanos', () => []);
    const meets = useState('timelineFeedMeets', () => []);
    const awaitingChallenges = useState('timelineFeedAwaitingChallenges', () => []);
    const upcomingFixtures = useState('timelineFeedUpcomingFixtures', () => []);
    const upcomingMeetDates = useState('timelineFeedUpcomingMeetDates', () => []);
    // Club matches being live-scored right now (Home "Live now" strip).
    const liveNow = useState('timelineFeedLiveNow', () => []);
    const resultsAwaitingCount = useState('timelineFeedResultsAwaitingCount', () => 0);
    const outstandingCharges = useState('timelineFeedOutstandingCharges', () => []);
    // Everything the member owes (R2 batch B): fees, Bill to requests, failed charges.
    const owed = useState('timelineFeedOwed', () => []);
    const accessApplicationAwaitingCount = useState('timelineFeedAccessApplicationAwaitingCount', () => 0);
    const accessApplicationSentCount = useState('timelineFeedAccessApplicationSentCount', () => 0);
    // Admins: players waiting to be placed across their clubs (JOIN_SETTINGS_PLAN.md D19).
    const pendingPlacementAwaitingCount = useState('timelineFeedPendingPlacementAwaitingCount', () => 0);
    const pendingPlacementCompetitions = useState('timelineFeedPendingPlacementCompetitions', () => []);
    // Set by the SignalR timelineupdated handler; the timeline shows a
    // "new updates" affordance instead of yanking the list mid-scroll.
    const pendingUpdates = useState('timelineFeedPendingUpdates', () => false);

    const pageSize = 20;

    const feedEmpty = computed(() => items.value.length === 0 && pinned.value.length === 0);

    const _fetchPage = async (cursor) => {
        const { httpRequest } = useHttpRequest();
        const query = cursor ? { cursor, pageSize } : { pageSize };
        return await httpRequest(endpoints.timeline.feed, query);
    };

    const refresh = async () => {
        if (feedLoading.value) return;
        feedLoading.value = true;
        feedErrors.value = null;
        try {
            const res = await _fetchPage(null);
            items.value = res.items ?? [];
            pinned.value = res.pinned ?? [];
            nextCursor.value = res.nextCursor ?? null;
            hasMore.value = !!res.hasMore;
            // First-page section data (drives the Home sections around the feed).
            managedClubs.value = res.managedClubs ?? [];
            leagues.value = res.leagues ?? [];
            ladders.value = res.ladders ?? [];
            tournaments.value = res.tournaments ?? [];
            americanos.value = res.americanos ?? [];
            meets.value = res.meets ?? [];
            awaitingChallenges.value = res.awaitingChallenges ?? [];
            upcomingFixtures.value = res.upcomingFixtures ?? [];
            upcomingMeetDates.value = res.upcomingMeetDates ?? [];
            liveNow.value = res.liveNow ?? [];
            resultsAwaitingCount.value = res.resultsAwaitingCount ?? 0;
            outstandingCharges.value = res.outstandingCharges ?? [];
            owed.value = res.owed ?? [];
            accessApplicationAwaitingCount.value = res.accessApplicationAwaitingCount ?? 0;
            accessApplicationSentCount.value = res.accessApplicationSentCount ?? 0;
            pendingPlacementAwaitingCount.value = res.pendingPlacementAwaitingCount ?? 0;
            pendingPlacementCompetitions.value = res.pendingPlacementCompetitions ?? [];
            pendingUpdates.value = false;
        } catch (e) {
            console.error('Timeline feed error:', e);
            feedErrors.value = e;
        } finally {
            feedLoading.value = false;
            feedLoaded.value = true;
        }
    };

    const loadMore = async () => {
        if (!hasMore.value || loadingMore.value || feedLoading.value) return;
        loadingMore.value = true;
        try {
            const res = await _fetchPage(nextCursor.value);
            const existingIds = new Set(items.value.map(item => item.id));
            items.value = items.value.concat((res.items ?? []).filter(item => !existingIds.has(item.id)));
            nextCursor.value = res.nextCursor ?? null;
            hasMore.value = !!res.hasMore;
        } catch (e) {
            console.error('Timeline feed loadMore error:', e);
            feedErrors.value = e;
        } finally {
            loadingMore.value = false;
        }
    };

    const dismiss = async (itemId) => {
        const { httpRequest } = useHttpRequest();
        dismissingLoading.value[itemId] = true;
        try {
            await httpRequest(endpoints.timeline.feedDismiss, { itemId });
            items.value = items.value.filter(item => item.id !== itemId);
            pinned.value = pinned.value.filter(item => item.id !== itemId);
        } catch (e) {
            console.error('Timeline feed dismissal failed:', e);
            throw e;
        } finally {
            dismissingLoading.value[itemId] = false;
        }
    };

    const notifyUpdated = () => {
        pendingUpdates.value = true;
    };

    // Refresh only the "Live now" slice — cheap enough for a poll or a SignalR nudge,
    // and it never disturbs the scrolled feed.
    const refreshLiveNow = async () => {
        try {
            const { httpRequest } = useHttpRequest();
            liveNow.value = (await httpRequest(endpoints.timeline.liveNow)) ?? [];
        } catch (e) {
            // transient — keep whatever we last had
        }
    };

    return {
        items,
        pinned,
        nextCursor,
        hasMore,
        feedLoading,
        feedLoaded,
        loadingMore,
        feedErrors,
        feedEmpty,
        dismissingLoading,
        pendingUpdates,
        managedClubs,
        leagues,
        ladders,
        tournaments,
        americanos,
        meets,
        awaitingChallenges,
        upcomingFixtures,
        upcomingMeetDates,
        liveNow,
        resultsAwaitingCount,
        outstandingCharges,
        owed,
        accessApplicationAwaitingCount,
        accessApplicationSentCount,
        pendingPlacementAwaitingCount,
        pendingPlacementCompetitions,
        refresh,
        refreshLiveNow,
        loadMore,
        dismiss,
        notifyUpdated
    };
}
